import { apiClientV1 } from '@fly/http-service';
import { deepSanitize } from '@fly/shared';
import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { GraphQLClient } from 'graphql-request';

const GQL_API_TAG_TYPES = [
  'repositories',
  'artifacts',
  'artifact-details',
  'artifact-files',
  'repository-names',
  'repository-workflows',
  'repository-release',
  'repository-releases',
  'environments',
] as const;

/**
 * Single GraphQL API slice using the v2 endpoint only.
 */
const createGraphQLApiSlice = () => {
  const v1Url = import.meta.env.VITE_APP_API_V1_URL;
  const v2Url =
    import.meta.env.VITE_APP_API_V2_URL || v1Url?.replace('/v1', '/v2');

  const graphqlUrl = `${v2Url}/graphql`;

  if (!v2Url) {
    console.error(
      'VITE_APP_API_V2_URL is not defined. Please check your environment configuration.'
    );
  }

  const graphqlBaseQuery = graphqlRequestBaseQuery({
    client: new GraphQLClient(
      `${typeof window !== 'undefined' ? window.location.origin : ''}${graphqlUrl}`,
      {
        jsonSerializer: { parse: (data) => data, stringify: (data) => data },

        fetch: async (url, init) => {
          const response = await apiClientV1.request({
            url: url.toString(),
            method: init?.method || 'POST',
            data: init?.body,
            signal: init?.signal || undefined,
          });

          return {
            ...response,
            ...response.request,
            ok: !response.data.errors,
            text: () => Promise.resolve(response.data),
            headers: new Headers([...(response.headers as any)]),
          };
        },
      }
    ),
  });

  const baseQuery = async (args: any, api: any, extraOptions: any) => {
    if (args.variables) {
      args = {
        ...args,
        variables: deepSanitize(args.variables),
      };
    }
    return graphqlBaseQuery(args, api, extraOptions);
  };

  return createApi({
    reducerPath: 'gqlApiSlice',
    baseQuery: baseQuery,
    endpoints: () => ({}),
    tagTypes: [...GQL_API_TAG_TYPES],
  });
};

export const gqlApiSlice = createGraphQLApiSlice();

export type GqlApiSliceTag = (typeof GQL_API_TAG_TYPES)[number];
